import styled, { keyframes } from "styled-components";
import { Colors } from "../../../../styles/colors";

const shimmer = keyframes`
	0% { background-position: -300px 0; }
	100% { background-position: 300px 0; }
`;

type AttentionTone =
	| "GRAVE"
	| "SIGNIFICATIVA"
	| "LEVE"
	| "IDENTIFICADO"
	| "SEM_ALERTA"
	| "DADOS_INSUFICIENTES"
	| "INDISPONIVEL"
	| "NEUTRAL";

const attentionColor = (tone: AttentionTone | null) => {
	if (tone === "GRAVE") return Colors.tertirary_red_1_dark;
	if (tone === "SIGNIFICATIVA" || tone === "LEVE")
		return Colors.tertiary_orange_1_dark;
	return Colors.neutral_grey_k_60;
};

const attentionBackground = (tone: AttentionTone) => {
	if (tone === "GRAVE") return `${Colors.tertirary_red_1_light}1f`;
	if (tone === "SIGNIFICATIVA" || tone === "LEVE")
		return `${Colors.tertiary_orange_1_light}1f`;
	return "#f6f7f9";
};

const attentionBorder = (tone: AttentionTone) => {
	if (tone === "GRAVE") return `${Colors.tertirary_red_1_light}80`;
	if (tone === "SIGNIFICATIVA" || tone === "LEVE")
		return `${Colors.tertiary_orange_1_light}80`;
	return "#e8eaf0";
};

export const ModalPaper = styled.div`
	background-color: #ffffff;
	border: 0;
	border-radius: 28px;
	width: min(876px, calc(100% - 24px));
	max-height: calc(100vh - 24px);
	box-shadow: 0 22px 70px rgba(0, 14, 75, 0.3);
	overflow-y: auto;
	display: flex;
	flex-direction: column;
	outline: none;
	position: relative;

	@media (max-width: 600px) {
		border-radius: 18px;
	}
`;

export const ModalTop = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 28px 30px;
	border-bottom: 1px solid #edf0f4;

	@media (max-width: 600px) {
		padding: 20px 18px;
	}
`;

export const TitleContainer = styled.div`
	display: flex;
	align-items: center;
	gap: 16px;

	svg {
		width: 24px;
		height: 24px;
		color: #151b26;
	}
`;

export const ModalTitle = styled.h2`
	font-family: "NotoSans-Bold", sans-serif;
	font-size: 27px;
	font-weight: 700;
	margin: 0;
	color: #151b26;

	@media (max-width: 600px) {
		font-size: 20px;
	}
`;

export const CloseButton = styled.button`
	border: 0;
	background: transparent;
	width: 36px;
	height: 36px;
	display: grid;
	place-items: center;
	border-radius: 50%;
	cursor: pointer;
	transition: background-color 0.2s ease;
	color: #000e4b;

	&:hover {
		background: #f3f5f8;
	}

	&:focus-visible {
		outline: 2px solid ${Colors.secondary_green_k_main};
		outline-offset: 1px;
	}

	svg {
		width: 20px;
		height: 20px;
	}
`;

export const ModalBody = styled.div`
	padding: 32px 30px 30px;

	@media (max-width: 600px) {
		padding: 24px 18px 20px;
	}
`;

export const SectionHeader = styled.div`
	display: flex;
	align-items: center;
	gap: 9px;
	margin-bottom: 17px;

	svg {
		width: 15px;
		height: 15px;
		color: #656e7b;
	}
`;

export const SectionTitle = styled.h3`
	font-family: "Inter-Bold", sans-serif;
	font-size: 14px;
	font-weight: 700;
	color: #656e7b;
	margin: 0;
	text-transform: uppercase;
	letter-spacing: 0.5px;
`;

export const InfoCard = styled.div`
	background-color: #f6f7f9;
	border-radius: 14px;
	border: 1px solid #e1e5eb;
	padding: 20px;
	display: flex;
	flex-direction: column;
	justify-content: center;
	gap: 7px;
	min-height: 86px;
	margin-bottom: 18px;
`;

export const CardLabel = styled.span`
	font-family: "Inter-Bold", sans-serif;
	font-size: 13px;
	font-weight: 700;
	color: #656e7b;
	text-transform: uppercase;
	letter-spacing: 0.25px;
`;

export const CardValue = styled.p`
	font-family: "Inter-Bold", sans-serif;
	font-size: 18px;
	line-height: 1.35;
	font-weight: 700;
	color: #151b26;
	margin: 0;

	@media (max-width: 600px) {
		font-size: 15px;
	}
`;

export const GridRow = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 16px;
	margin-bottom: 18px;

	@media (max-width: 600px) {
		grid-template-columns: 1fr;
	}
`;

export const MetricCard = styled.div`
	background-color: #f6f7f9;
	border-radius: 14px;
	border: 1px solid #e1e5eb;
	padding: 20px;
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	gap: 7px;
	min-height: 116px;

	small {
		font-family: "NotoSans-Regular", sans-serif;
		font-size: 10px;
		color: #797d8d;
		margin-top: auto;
	}
`;

export const PainValue = styled(CardValue)<{ $highlight: boolean }>`
	color: ${({ $highlight }) =>
		$highlight ? Colors.tertiary_orange_1_dark : "#151b26"};
`;

export const SeeMoreButton = styled.button`
	align-self: flex-start;
	border: 0;
	background: transparent;
	color: ${Colors.neutral_grey_k_40};
	font-family: "NotoSans-Bold", sans-serif;
	font-size: 12px;
	font-weight: 700;
	text-decoration: underline;
	padding: 0;
	cursor: pointer;
`;

export const AttentionBox = styled.div<{ $tone: AttentionTone }>`
	background-color: ${({ $tone }) => attentionBackground($tone)};
	border-radius: 14px;
	border: 1px solid ${({ $tone }) => attentionBorder($tone)};
	padding: 20px;
	display: flex;
	flex-direction: column;
	justify-content: center;
	gap: 7px;
	min-height: 84px;

	button {
		align-self: flex-start;
		border: 0;
		background: transparent;
		color: ${Colors.secondary_button_default};
		font-family: "NotoSans-Bold", sans-serif;
		font-size: 12px;
		font-weight: 700;
		text-decoration: underline;
		padding: 0;
		cursor: pointer;
	}
`;

export const AttentionLabel = styled.span<{ $tone: AttentionTone }>`
	font-family: "Inter-Bold", sans-serif;
	font-size: 13px;
	font-weight: 700;
	color: ${({ $tone }) => attentionColor($tone)};
	text-transform: uppercase;
	letter-spacing: 0.25px;
`;

export const AttentionValue = styled.p<{ $tone: AttentionTone }>`
	font-family: "Inter-Bold", sans-serif;
	font-size: 17px;
	line-height: 1.35;
	font-weight: 700;
	color: ${({ $tone }) => attentionColor($tone)};
	margin: 0;

	@media (max-width: 600px) {
		font-size: 15px;
	}
`;

export const LocalizedState = styled.div`
	min-height: 356px;
	background: #f6f7f9;
	border: 1px solid #e8eaf0;
	border-radius: 14px;
	margin-bottom: 18px;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 12px;
	color: #656e7b;
	font-family: "NotoSans-Regular", sans-serif;
	font-size: 13px;

	p {
		margin: 0;
		text-align: center;
	}

	button {
		border: 0;
		background: transparent;
		color: ${Colors.secondary_button_default};
		font: inherit;
		font-weight: 700;
		text-decoration: underline;
		cursor: pointer;
	}
`;

export const SkeletonLine = styled.div<{ $width: string }>`
	height: 15px;
	width: ${({ $width }) => $width};
	min-width: 80px;
	border-radius: 7px;
	background: linear-gradient(90deg, #e9ecf1 25%, #dce1e8 50%, #e9ecf1 75%);
	background-size: 600px 100%;
	animation: ${shimmer} 1.3s infinite linear;
`;
